export default function Settings() {
  return (
    <section id="settings">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Settings</h2>
      <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
        Configure the look and behaviour of Bedrock Toolchest. Open Settings from the menu bar.
      </p>

      {/* Appearance */}
      <div id="settings-appearance" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Appearance</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <ul className="space-y-2">
            <li>
              <strong className="text-slate-800 dark:text-slate-200">Theme</strong> — Choose from 10
              different UI themes. If no theme is selected, the program detects whether your OS is in
              light or dark mode and uses <em>Cotton</em> or <em>Onyx</em> respectively.
            </li>
            <li>
              <strong className="text-slate-800 dark:text-slate-200">UI Scale</strong> — Increase or
              decrease the font size to suit your display.
            </li>
            <li>
              <strong className="text-slate-800 dark:text-slate-200">Language</strong> — Choose the
              display language for the program. On first launch, the app detects your operating
              system language and automatically selects it if a matching translation is available.
              The program needs to restart to apply a language change, so make sure you have saved
              any changes to your world before switching.
            </li>
          </ul>
          <div className="rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 p-3">
            <p className="text-blue-700 dark:text-blue-400 text-xs">
              You can open the languages folder to view all installed language files. These can be
              used as templates to create your own translations — share them with the community on
              Discord!
            </p>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div id="settings-data" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Data Management</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <ul className="space-y-2">
            <li>
              <strong className="text-slate-800 dark:text-slate-200">Clear Recent Worlds</strong> — Clears
              the list of recently opened worlds shown on the start screen.
            </li>
            <li>
              <strong className="text-slate-800 dark:text-slate-200">Custom World Folders</strong> — Add
              your own folders to include in the All Worlds list on the start screen. Useful if you
              store worlds in custom locations outside of the standard Minecraft folders. Worlds from
              all detected installations (Bedrock, Preview, and Education Edition) are shown
              automatically, grouped by source.
            </li>
            <li>
              <strong className="text-slate-800 dark:text-slate-200">Enable Backups</strong> — Controls
              whether automatic backups are created before save operations. Enabled by default. Disabling
              this shows a confirmation warning and a red label in the settings as a reminder that your
              worlds are not being backed up.
            </li>
            <li>
              <strong className="text-slate-800 dark:text-slate-200">Backup Retention</strong> — Set the
              maximum number of automatic backups to keep per world (1–10, default 5). Once the limit
              is reached, the oldest automatic backup is removed when a new one is created. Manual
              backups you create from the Backups tab are exempt from this limit and are never
              automatically deleted.
            </li>
          </ul>
          <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 p-3">
            <p className="text-red-700 dark:text-red-400 text-xs">
              Disabling automatic backups means your world will not be backed up before save
              operations, chunk pruning, dimension resets, or database compaction. This is not
              recommended unless you are managing backups yourself.
            </p>
          </div>
        </div>
      </div>

      {/* Check for Updates */}
      <div id="settings-updates" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Check for Updates</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <ul className="space-y-2">
            <li>
              <strong className="text-slate-800 dark:text-slate-200">Automatic check</strong> — Each
              time Bedrock Toolchest starts, it silently checks for a newer version in the background.
              If an update is available, a notification appears letting you know. This check has no
              impact on startup time and requires no action from you.
            </li>
            <li>
              <strong className="text-slate-800 dark:text-slate-200">Manual check</strong> — You can
              also click the <em>Check for Updates</em> button in Settings at any time to check on
              demand. If an update is found, you'll be given a link to download it.
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
